import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import CustomModal from "../widgets/CustomModal";
import { HeadingContext } from "../context/HeadingContext";
import {
  getCards,
  addCard,
  setDefaultCard,
  deleteCard,
  getStripeConnectionStatus,
  connectStripe,
} from "../_slice/paymentMethodSlice";
import AppContext from "../context/AppContext";
import {
  formatApiDateTime,
  formatCardExpiry,
  getCardBrandColor,
  getCardBrandLabel,
  maskedCardNumber,
  normalizeCard,
  toastHandler,
} from "../../utils/utils";
import deleteImg from "../../assets/images/delete.png";

const elementBaseStyle = {
  base: {
    fontSize: "14px",
    color: "#292929",
    fontFamily: "inherit",
    "::placeholder": { color: "#9CA3AF" },
  },
  invalid: { color: "#9e2146" },
};


// Add Card Form using Stripe Elements
function AddCardForm({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: { cardholder_name: "" },
  });

  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);

  const onSubmit = async (data) => {
    if (!stripe || !elements) return;

    setProcessing(true);
    setCardError(null);

    const cardNumberElement = elements.getElement(CardNumberElement);

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
      billing_details: { name: data.cardholder_name },
    });

    if (error) {
      setCardError(error.message);
      setProcessing(false);
      return;
    }

    dispatch(
      addCard({
        paymentMethodId: paymentMethod.id,
        navigate,
        setLoader,
      })
    ).then((response) => {
      setProcessing(false);
      if (response?.type?.endsWith("/fulfilled")) {
        reset();
        elements.getElement(CardNumberElement)?.clear();
        elements.getElement(CardExpiryElement)?.clear();
        elements.getElement(CardCvcElement)?.clear();
        onSuccess();
      }
    });
  };

  const fieldStyle = {
    padding: "10px 12px",
    height: "42px",
    display: "flex",
    alignItems: "center",
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h3
        className="text-center mb-4"
        style={{ fontSize: "18px", fontWeight: "700", color: "#292929" }}
      >
        Add Card
      </h3>

      <div className="form-group mb-3">
        <label className="innerlable">Cardholder Name</label>
        <input
          type="text"
          className="form-control"
          placeholder="Jil Doe"
          {...register("cardholder_name", {
            required: "Cardholder name is required",
          })}
        />
        {errors.cardholder_name && (
          <p className="text-danger">{errors.cardholder_name.message}</p>
        )}
      </div>

      <div className="form-group mb-3">
        <label className="innerlable">Card Number</label>
        <div className="form-control" style={fieldStyle}>
          <CardNumberElement
            options={{ style: elementBaseStyle, showIcon: true }}
            className="w-100"
          />
        </div>
      </div>

      <div className="row">
        <div className="col-6">
          <div className="form-group mb-3">
            <label className="innerlable">Expiry Date</label>
            <div className="form-control" style={fieldStyle}>
              <CardExpiryElement
                options={{ style: elementBaseStyle }}
                className="w-100"
              />
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="form-group mb-3">
            <label className="innerlable">CVV</label>
            <div className="form-control" style={fieldStyle}>
              <CardCvcElement
                options={{ style: elementBaseStyle }}
                className="w-100"
              />
            </div>
          </div>
        </div>
      </div>

      {cardError && <p className="text-danger mt-2">{cardError}</p>}

      <div className="d-flex justify-content-center mt-4">
        <button
          type="submit"
          className="submit_btn"
          disabled={!stripe || processing}
          style={{ minWidth: "120px" }}
        >
          {processing ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}

function PaymentMethod() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);
  const { setHeading } = useContext(HeadingContext);

  const {
    cards,
    publicKey,
    connectionStatus,
    connectionChecked,
    defaultPaymentMethod,
    cardsLoaded,
  } = useSelector((state) => state.paymentMethod);

  const [stripePromise, setStripePromise] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, id: "" });
  const [showAddCard, setShowAddCard] = useState(false);
  // Payment method id whose row action is in flight, so only that row is busy
  const [busyCard, setBusyCard] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const isConnected = Boolean(connectionStatus);

  // The backend answers with `default_payment_method`; fall back to the
  // per-card flag so exactly one card is ever marked default.
  const savedCards = (cards || [])
    .map(normalizeCard)
    .filter((card) => card.id)
    .map((card) => ({
      ...card,
      isDefault: defaultPaymentMethod
        ? card.id === defaultPaymentMethod
        : card.isDefault,
    }));

  useEffect(() => {
    setHeading("Payment Method");
  }, [setHeading]);

  // Connection state decides what this screen shows, so read it first and
  // only ask for cards once the organisation is actually connected.
  useEffect(() => {
    dispatch(getStripeConnectionStatus({ navigate, setLoader })).then(
      (response) => {
        const payload = response?.payload;
        if (payload?.is_connected || payload?.connected) {
          dispatch(getCards({ navigate, setLoader }));
        }
      }
    );
  }, []);

  // Initialize Stripe — prefer publicKey from API, fall back to env
  useEffect(() => {
    const key = publicKey || process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (key && !stripePromise) {
      setStripePromise(loadStripe(key));
    }
  }, [publicKey, stripePromise]);

  const refreshCards = () => {
    dispatch(getCards({ navigate, setLoader }));
  };

  // POST /admin/stripe/connect is idempotent, so this is safe to retry.
  const handleConnect = () => {
    if (connecting) return;
    setConnecting(true);
    dispatch(connectStripe({ navigate, setLoader })).then((response) => {
      setConnecting(false);
      if (response?.type?.endsWith("/fulfilled")) {
        toastHandler(
          true,
          response.payload?.message || "Connected to Stripe successfully"
        );
        refreshCards();
      }
    });
  };

  const handleSetDefault = (paymentMethodId) => {
    if (busyCard) return;
    setBusyCard(paymentMethodId);
    dispatch(setDefaultCard({ paymentMethodId, navigate, setLoader })).then(
      (response) => {
        setBusyCard(null);
        // Only refetch on success; a rejection has already raised a toast and
        // refetching would just discard the error state.
        if (response?.type?.endsWith("/fulfilled")) refreshCards();
      }
    );
  };

  const handleDelete = () => {
    const id = deleteModal.id;
    setDeleteModal({ show: false, id: "" });
    setBusyCard(id);
    dispatch(deleteCard({ paymentMethodId: id, navigate, setLoader })).then(
      (response) => {
        setBusyCard(null);
        if (response?.type?.endsWith("/fulfilled")) refreshCards();
      }
    );
  };

  const handleAddCardSuccess = () => {
    setShowAddCard(false);
    refreshCards();
  };

  return (
    <>
      <div className="org">
        <div className="org_header d-flex align-items-center justify-content-between">
          <h1 className="org_header_heading">Payment Method</h1>
        </div>

        <div style={{ maxWidth: "720px" }}>
          {/* Connection state — everything else depends on it */}
          {connectionChecked && (
            <div
              className="d-flex align-items-center justify-content-between flex-wrap mt-3 mb-3"
              style={{
                background: isConnected ? "#F0F7F2" : "#FEF3C7",
                border: `1px solid ${isConnected ? "#BEE0CF" : "#FDE68A"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                gap: "12px",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isConnected ? "#1F7A45" : "#92400E",
                }}
              >
                {isConnected
                  ? "Stripe connected"
                  : "Stripe is not connected."}
              </span>
              {!isConnected && (
                <button
                  type="button"
                  className="submit_btn"
                  onClick={handleConnect}
                  disabled={connecting}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {connecting ? "Connecting..." : "Connect to Stripe"}
                </button>
              )}
            </div>
          )}

          {isConnected && (
            <>
              {/* Card List */}
              <div className="mt-3">
                {savedCards.length > 0
                  ? savedCards.map((card) => {
                      const rowBusy = busyCard === card.id;
                      return (
                        <div
                          key={card.id}
                          className="d-flex align-items-center flex-wrap mb-3"
                          style={{
                            background: "#fff",
                            border: `1px solid ${
                              card.isDefault ? "#004D26" : "#E5E7EB"
                            }`,
                            borderRadius: "8px",
                            padding: "14px 18px",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              minWidth: "70px",
                              fontWeight: "700",
                              color: getCardBrandColor(card.brand),
                              fontSize: "15px",
                            }}
                          >
                            {getCardBrandLabel(card.brand)}
                          </div>

                          <div
                            className="flex-grow-1"
                            style={{ minWidth: "180px" }}
                          >
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#292929",
                                letterSpacing: "1px",
                              }}
                            >
                              {maskedCardNumber(card.last4)}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6B7280",
                                marginTop: "2px",
                              }}
                            >
                              Expires{" "}
                              {formatCardExpiry(card.expMonth, card.expYear)}
                              {card.createdAt
                                ? ` · Added ${formatApiDateTime(card.createdAt)}`
                                : ""}
                            </div>
                          </div>

                          <div
                            className="d-flex align-items-center flex-wrap"
                            style={{ gap: "10px" }}
                          >
                            {card.isDefault ? (
                              <>
                                <span
                                  style={{
                                    background: "#DCFCE7",
                                    color: "#166534",
                                    borderRadius: "12px",
                                    padding: "4px 12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Default
                                </span>
                                {/* The default card cannot be deleted, so the
                                    action is disabled rather than failing. */}
                                <span
                                  style={{ fontSize: "11px", color: "#6B7280" }}
                                  title="Set another card as default before deleting this one."
                                >
                                  Set another card as default to delete this one
                                </span>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="action_btns"
                                  onClick={() => handleSetDefault(card.id)}
                                  disabled={!!busyCard}
                                  style={{
                                    whiteSpace: "nowrap",
                                    opacity: busyCard ? 0.6 : 1,
                                  }}
                                >
                                  {rowBusy ? "Working..." : "Set as Default"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDeleteModal({ show: true, id: card.id })
                                  }
                                  disabled={!!busyCard}
                                  style={{
                                    background: "#F97316",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "8px 10px",
                                    cursor: busyCard ? "not-allowed" : "pointer",
                                    opacity: busyCard ? 0.6 : 1,
                                  }}
                                  title="Delete card"
                                >
                                  <img
                                    src={deleteImg}
                                    alt="delete"
                                    style={{
                                      width: "14px",
                                      height: "14px",
                                      filter: "brightness(0) invert(1)",
                                    }}
                                  />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  : cardsLoaded && (
                      <p
                        className="mb-3"
                        style={{ color: "#6B7280", fontSize: "13px" }}
                      >
                        No saved cards yet.
                      </p>
                    )}
              </div>

              {/* Add card toggle */}
              <button
                type="button"
                className="action_btns"
                onClick={() => setShowAddCard((open) => !open)}
                style={{
                  color: "#004D26",
                  fontWeight: "600",
                  fontSize: "13px",
                  marginTop: "8px",
                  marginBottom: "12px",
                  padding: 0,
                }}
              >
                {showAddCard ? "Cancel" : "+ Add Card"}
              </button>

              {/* Add Card Form (Stripe Elements) */}
              {showAddCard && (
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    padding: "24px",
                  }}
                >
                  {stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <AddCardForm onSuccess={handleAddCardSuccess} />
                    </Elements>
                  ) : (
                    <p className="mb-0" style={{ color: "#6B7280" }}>
                      Loading payment form...
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <CustomModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, id: "" })}
        size="md"
        body={
          <div className="deleteModal">
            <p className="text-center mb-5">
              Are you sure you want to delete this card?
            </p>
            <div className="d-flex justify-content-between align-items-center custom_bottom_padding">
              <button
                type="button"
                className="submit_btn"
                onClick={handleDelete}
              >
                Yes
              </button>
              <button
                type="button"
                className="cancleBtn"
                onClick={() => setDeleteModal({ show: false, id: "" })}
              >
                No
              </button>
            </div>
          </div>
        }
      />
    </>
  );
}

export default PaymentMethod;
